// ** Axios Imports
import axios from "axios"
import { getCookie, getISODateUTC, sleep } from "./util.js"

import { CONTROLLERS } from "./constants"
// const API_OPUS = process.env.REACT_APP_API_OPUS || "/api"
// const API_CRM = process.env.REACT_APP_API_CRM || "/api"
// const API_VISITS = process.env.REACT_APP_API_VISITS || "/api"
// const API_BILLING = process.env.REACT_APP_API_BILLING || "/api"
// const API_CONFIGURATOR = process.env.REACT_APP_API_CONFIGURATOR || "/Configurator/api"

// LOCAL HELPERS ----------------------------
// delete the item if it has been removed from the form
const deleteNonPresentItem = async (dbItems, formItems, controller) => {
  for (let index = 0; index < dbItems.length; index++) {
    const dbItem = dbItems[index]
    const itemExists = formItems.find((formItem) => formItem.id === dbItem.id)
    if (!itemExists && dbItem.id) {
      const response = await axios.delete(`${CONTROLLERS.crm.endpoint}/${controller}/${dbItem.id}`)
      console.log(`deleted item ${dbItem.id}`, response)
    }
  }
}
// add or update an item depending on the id's presence
const updateOrAddItem = async (formItems, controller) => {
  for (let index = 0; index < formItems.length; index++) {
    const item = formItems[index]
    if (item.id) {
      await axios.put(`${CONTROLLERS.crm.endpoint}/${controller}/${item.id}`, item)
    } else {
      await axios.post(`${CONTROLLERS.crm.endpoint}/${controller}`, item)
    }
  }
}

// GET --------------------------------
/**
 * Generic function to get all items from a controller
 * @param {String} controller Controller name
 * @param {String} apiUrl "crm" | "visits" | "billing" | "config" | "calendar"
 * @returns {Array} Array of items
 */
export const getAllFromController = async (controller, apiUrl = "crm") => {
  let url = CONTROLLERS.crm.endpoint
  if (apiUrl === "visits") url = CONTROLLERS.visits.endpoint
  if (apiUrl === "billing") url = CONTROLLERS.billing.endpoint
  if (apiUrl === "config") url = CONTROLLERS.configurator.endpoint
  if (apiUrl === "opus") url = CONTROLLERS.opus.endpoint

  try {
    const response = await axios.get(`${url}/${controller}`)
    return response.data
  } catch (error) {
    console.error(error)
  }
}
export const getPersonHistory = async (personId) => {
  try {
    const events = []
    const interactions = (
      await axios.get(`${CONTROLLERS.visits.endpoint}/Interactions?filters[personId]=${personId}`)
    ).data.data
    // using 'for' instead of '.map' because it is promise-aware
    for (let index = 0; index < interactions.length; index++) {
      const interactionsEvents = await axios.get(
        `${CONTROLLERS.visits.endpoint}/Events?filters[interactionId]=${interactions[index].id}&sort[on]=DESC`
      )
      events.push(...interactionsEvents.data.data)
    }
    return events
  } catch (error) {
    console.error(error)
  }
}
// get an interaction between a person and a company
export const getPersonInteractions = async (personId, internalCompanyId) => {
  try {
    const interactions = (
      await axios.get(
        `${CONTROLLERS.visits.endpoint}/Interactions?filters[personId]=${personId}&filters[internalCompanyId]=${internalCompanyId}`
      )
    ).data.data
    return interactions
  } catch (error) {
    console.error(error)
  }
}

// get informations about the logged in user with an id (from Identity)
export const getLoggedInUser = async () => {
  try {
    const userId = getCookie("user.ID", false)
    if (!userId) throw new Error("No user ID found in cookies")
    const response = await axios.get(`${CONTROLLERS.opus.endpoint}/Users/${userId}`)
    const formatedRoles = {}
    response.data.roles.map((role) => {
      formatedRoles[role.name] = role.state
    })
    return { ...response.data, roles: formatedRoles }
  } catch (error) {
    // console.error(error)
    // throw new Error(error)
    return { error }
  }
}

export const getShowrooms = async () => {
  try {
    const response = await axios.get(`${CONTROLLERS.crm.endpoint}/Showrooms`)
    return response.data
  } catch (error) {
    console.error(error)
  }
}

// POST --------------------------------
// Final function to add a person to the CRM
export const postVisitForm = async (personalInfos, formEmails, formPhones, formAddresses) => {
  // internal helper method
  const addSecondaryInfos = async (formItems, controller, personId) => {
    for (let index = 0; index < formItems.length; index++) {
      const item = formItems[index]
      const itemWithId = { ...item, entityId: personId }
      await axios.post(`${CONTROLLERS.crm.endpoint}/${controller}`, itemWithId)
    }
  }

  // adding base user
  const { interactionStateId, interactionOriginId, ...cleanPersonData } = personalInfos
  const resPersonalInfos = await axios.post(`${CONTROLLERS.crm.endpoint}/Persons`, cleanPersonData)
  if (resPersonalInfos.status !== 201) {
    throw new Error("Error while adding user")
  }
  // on success, get this person's id
  const personId = resPersonalInfos.data.id
  // ! Vulnerability : since we POST on 2 differents APIs, we need to wait
  // for RabbitMQ to replicate the data before adding the first event. From the front
  // it's not possible to know when the data is replicated, so we try 5x during 10sec
  let resInteraction = {}
  const MAX_TRIES = 5
  for (let index = 1; index <= MAX_TRIES; index++) {
    sleep(2000)
    try {
      // try adding interaction
      resInteraction = await axios.post(`${CONTROLLERS.visits.endpoint}/Interactions`, {
        interactionStateId,
        interactionOriginId,
        personId,
        internalCompanyId: cleanPersonData.internalCompanyId
      })
      if (resInteraction.status === 201) {
        // add initial event (account creation)
        axios.post(`${CONTROLLERS.visits.endpoint}/Events`, {
          on: getISODateUTC(),
          description: "Initial account creation.",
          eventReasonId: 1,
          interactionId: resInteraction.data.id,
          showroomId: cleanPersonData.showroomId,
          collaboratorId: cleanPersonData.collaboratorId
        })
        break
      }
      if (index === MAX_TRIES) {
        throw new Error("Error while adding interaction")
      }
    } catch (err) {
      console.error(err)
    }
  }
  try {
    Promise.all([
      // add items depending on their id existence
      addSecondaryInfos(formPhones, "Phones", personId),
      addSecondaryInfos(formEmails, "Emails", personId),
      addSecondaryInfos(formAddresses, "Addresses/Privates", personId)
    ])
    return resPersonalInfos
  } catch (err) {
    console.error(err)
  }
}
// add an event to a person's history
export const addEventInPersonHistory = async (event) => {
  try {
    const response = await axios.post(`${CONTROLLERS.visits.endpoint}/Events`, event)
    return response
  } catch (error) {
    console.error(error)
  }
}

export const deleteEventInPersonHistory = async (eventId) => {
  try {
    const response = await axios.delete(`${CONTROLLERS.visits.endpoint}/Events/${eventId}`)
    return response
  } catch (error) {
    console.error("Error updating event:", error)
    throw error // Re-throw the error to handle it further up the call stack if needed
  }
}

// Configurator endpoints
// post offer/sale to configurator API
export const postOfferSaleToConfigurator = async (offerOrSale) => {
  try {
    const response = await axios.post(`${CONTROLLERS.configurator.endpoint}/OrdersAPI`, offerOrSale)
    return response.data
  } catch (error) {
    console.error(error)
  }
}
// post a direct sale to configurator API
export const postDirectSale = async (baseData) => {
  try {
    const response = await axios.post(
      `${CONTROLLERS.configurator.endpoint}/OrdersAPI/DirectSale`,
      baseData
    )
    return response.data
  } catch (error) {
    console.error(error)
  }
}
// post a price quote to configurator API
export const postPriceQuote = async (baseData) => {
  try {
    const response = await axios.post(
      `${CONTROLLERS.configurator.endpoint}/OrdersAPI/PriceQuote`,
      baseData
    )
    return response.data
  } catch (error) {
    console.error(error)
  }
}

// PUT --------------------------------
// directly validate a person
export const validatePerson = async (personId) => {
  try {
    const response = await axios.patch(`${CONTROLLERS.crm.endpoint}/Persons/${personId}/Validate`)
    return response
  } catch (error) {
    console.error(error)
  }
}

// Final function to update a person/visit
export const putVisitForm = async (
  personalInfos,
  formEmails,
  formPhones,
  formAddresses,
  action,
  personId
) => {
  try {
    // update person infos
    const resPersonalInfos = await axios.put(
      `${CONTROLLERS.crm.endpoint}/Persons/${personId}`,
      personalInfos
    )
    // continue only on success
    if (resPersonalInfos.status === 204) {
      // get person's data from DB to be used in the next step
      const personData = await axios.get(`${CONTROLLERS.crm.endpoint}/Persons/${personId}`)
      const interactionData = await axios.get(
        `${CONTROLLERS.visits.endpoint}/Interactions?filters[personId]=${personId}&filters[internalCompanyId]=${personalInfos.internalCompanyId}`
      )
      const interactionId = interactionData.data.data[0].id // we assume the first interaction is the one we want
      Promise.all([
        // wait on deleting what's in DB, replacing by what's in the form
        deleteNonPresentItem(personData.data.emails, formEmails, "Emails"),
        deleteNonPresentItem(personData.data.phones, formPhones, "Phones"),
        deleteNonPresentItem(personData.data.addresses, formAddresses, "Addresses")
      ])
      Promise.all([
        // update or add items depending on their id existence
        updateOrAddItem(formPhones, "Phones"),
        updateOrAddItem(formEmails, "Emails"),
        updateOrAddItem(formAddresses, "Addresses/Privates"),
        // PUT interaction
        axios.put(`${CONTROLLERS.visits.endpoint}/Interactions/${interactionId}`, {
          id: interactionId,
          interactionStateId: personalInfos.interactionStateId,
          interactionOriginId: personalInfos.interactionOriginId,
          personId,
          internalCompanyId: personalInfos.internalCompanyId
        })
      ])
      if (action.toLowerCase() === "validate") await validatePerson(personId)
      return resPersonalInfos
    } else {
      throw new Error("Error while updating user's personal infos")
    }
  } catch (err) {
    console.error(err)
  }
}
// update an event in a person's history
export const editEventInPersonHistory = async (event) => {
  try {
    const response = await axios.put(`${CONTROLLERS.visits.endpoint}/Events/${event.id}`, event)
    return response.data
  } catch (error) {
    console.error(error)
  }
}

// DELETE --------------------------------
// delete an item with a given id
export const deleteById = async (id, endpoint) => {
  let api = CONTROLLERS.crm.endpoint
  if (endpoint.includes("Events")) api = CONTROLLERS.visits.endpoint
  if (endpoint.includes("OrdersAPI")) api = CONTROLLERS.configurator.endpoint

  try {
    const res = await axios.delete(`${api}/${endpoint}/${id}`)
    return res
  } catch (error) {
    console.error(error)
  }
}

export const getProduct = async (typeProduct) => {
  const endpoint = CONTROLLERS.configurator.endpoint
  try {
    const product = (await axios.get(`${endpoint}/ProductsAPI/Products/${typeProduct}`)).data.data
    return product
  } catch (error) {
    console.error(error)
  }
}

export const getProductModel = async (typeProduct) => {
  const endpoint = CONTROLLERS.configurator.endpoint
  try {
    const response = await fetch(`${endpoint}/ProductsAPI/Products/${typeProduct}`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json"
      }
    })

    if (!response.ok) {
      const errorData = await response.json()
      throw new Error(errorData.message || "Error while fetching products")
    }

    const data = await response.json()
    return data
  } catch (error) {
    console.error("Error fetching products:", error)
  }
}

export const getProductSize = async (typeProduct, productId) => {
  const endpoint = CONTROLLERS.configurator.endpoint
  try {
    const response = await fetch(
      `${endpoint}/ProductsAPI/Sizes?productId=${productId}&productType=${typeProduct}`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        }
      }
    )

    if (!response.ok) {
      const errorData = await response.json()
      throw new Error(errorData.message || "Error while fetching products")
    }

    const data = await response.json()
    return data
  } catch (error) {
    console.error("Error fetching products:", error)
  }
}

export const getPosts = async () => {
  try {
    const response = await fetch(`https://jsonplaceholder.typicode.com/posts`, {
      headers: {
        "Content-Type": "application/json"
      },
      method: "GET"
    })

    if (!response.ok) {
      const errorData = await response.json()
      throw new Error(errorData.message || "Error while fetching posts")
    }

    const data = await response.json()
    return data
  } catch (error) {
    console.error("Error fetching posts:", error)
    throw error
  }
}

export const sendEmailComplaint = async (payload) => {
  const endpoint = `${CONTROLLERS.crm.endpoint}/Persons/SendEmail`

  try {
    const formData = new FormData()
    formData.append("OrderNumber", payload.OrderNumber)
    formData.append("Showroom", payload.Showroom)
    formData.append("DeliveryDate", payload.DeliveryDate.toString())
    formData.append("FirstName", payload.FirstName)
    formData.append("LastName", payload.LastName)
    formData.append("Address", payload.Address)
    formData.append("Email", payload.Email)
    formData.append("PhoneNumber", payload.PhoneNumber)
    formData.append("ProductType", payload.ProductType)
    formData.append("Link", payload.Link)

    // Append product details
    for (const [key, value] of Object.entries(payload.ProductDetails)) {
      formData.append(`ProductDetails[${key}]`, value)
    }

    if (payload.Attachments && payload.Attachments.length > 0) {
      payload.Attachments.forEach((file) => {
        formData.append("Attachments", file)
      })
    }

    // Add attachments (if any) to formData
    // Assuming you have files to upload. If not, you can skip this part.
    // formData.append('Attachments', fileInput.files[0]);

    // Make the API call
    const response = await axios.post(endpoint, formData, {
      headers: {
        "Content-Type": "multipart/form-data"
      }
    })

    return response
  } catch (error) {
    console.error("Error sending complaint:", error)
    alert("Error sending complaint. Check the console for more details.")
  }
}
